<template>
  <!-- Primary Texts Section (shared across all ads) -->
  <div class="primary-texts-section">
    <div class="form-label">{{ $t('campaigns.facebook.primary_texts') }}</div>
    <div class="d-grid gap-2">
      <div v-for="(primaryText, textIndex) in sharedPrimaryTexts" :key="textIndex" class="primary-text-item">
        <LeadInput
          :value="primaryText"
          @update:model-value="(value) => updatePrimaryText(textIndex, value)"
          type="textarea"
          rows="5" />
        <div class="remove-absolute">
          <button
            v-if="sharedPrimaryTexts.length > 1"
            class="delete-btn-absolute"
            @click="() => removePrimaryText(textIndex)">
            <IconTrash :size="16" />
          </button>
        </div>
      </div>
    </div>

    <div class="mt-2">
      <LeadButton variant="secondary" @click="addNewTextarea">
        <template #prepend-icon>
          <IconPlus size="16" />
        </template>
        {{ $t('campaigns.facebook.add_primary_text') }}
      </LeadButton>
    </div>
  </div>

  <!-- Ads Section (for image placements) -->
  <div class="other-name-section">
    <div v-for="(ad, adIndex) in ads" :key="adIndex" class="ad-container">
      <div class="other-name-header">
        <div class="form-label">Ad {{ adIndex + 1 }} - {{ $t('campaigns.facebook.image_selection') }}</div>
      </div>

      <div class="images-grid">
        <AdImagePicker
          v-if="getSelectedImageId(adIndex, 'box')"
          v-bind="getBannerStateByImageId(getSelectedImageId(adIndex, 'box'))"
          boxSize
          @click="() => openPicker(adIndex, 'box')"
          @remove="() => removeBoxFormat(ad)" />
        <AdImagePicker v-else boxSize uploadText="1 : 1" @click="() => openPicker(adIndex, 'box')" />

        <template v-if="showPortraitImage(adIndex)">
          <AdImagePicker
            v-if="getSelectedImageId(adIndex, 'portrait')"
            v-bind="getBannerStateByImageId(getSelectedImageId(adIndex, 'portrait'))"
            boxSize
            @click="() => openPicker(adIndex, 'portrait')"
            @remove="() => removePortraitFormat(ad)" />
          <div v-else class="position-relative">
            <AdImagePicker boxSize uploadText="9 : 16" @click="() => openPicker(adIndex, 'portrait')" />
            <button
              class="delete-btn-absolute"
              @click.stop="() => removePortraitFormat(ad)"
              :title="$t('campaigns.facebook.remove_portrait_format')">
              <IconTrash :size="20" />
            </button>
          </div>
        </template>
        <AdImagePicker v-else boxSize uploadText="Story" @click="() => addStoryFormat(adIndex)" />
      </div>

      <div v-if="flatErrors.length > 0">
        <FormErrorList :errors="flatErrors" />
      </div>
    </div>
  </div>

  <ImagePickerModal
    v-model="designerModal"
    :images="pickerImages"
    :selectedIds="currentlySelectedIds"
    :title="$t('campaigns.choose_image')"
    :uploadText="$t('campaigns.choose_image')"
    @select="selectImageById" />
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import LeadButton from '../generic/LeadButton.vue';
import LeadInput from './LeadInput.vue';
import { IconPlus, IconTrash } from '@tabler/icons-vue';
import AdImagePicker from './AdImagePicker.vue';
import { FormErrorList } from '@prospective/lithium';
import type { IBanner } from '@/types/banner';
import { fetchOrderBanners } from '@/api/banner';
import { useToast } from 'vue-toastification';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';
import { type Ad, type AdPlacement } from '@/types/types';
import translator from '@/locale/translator';
import ImagePickerModal from './ImagePickerModal.vue';
import { bannerDisplayState, bannerThumbnailState } from '@/helpers/bannerState';

const props = defineProps<{
  modelValue: Array<any>;
  campaignId: number;
  errors?: any;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: Array<any>];
}>();

// because we get the errors in a nested object, we flatten them
const flatErrors = computed(() => {
  if (!props.errors) return [];
  if (Array.isArray(props.errors)) return props.errors;

  const extractErrors = (obj: any): string[] => {
    const result: string[] = [];
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        result.push(...obj[key]);
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        result.push(...extractErrors(obj[key]));
      } else if (typeof obj[key] === 'string') {
        result.push(obj[key]);
      }
    }
    return result;
  };

  return extractErrors(props.errors);
});

const ads = ref<Ad[]>([
  {
    id: null,
    primary_texts: [''],
    individual_placement: false,
    ad_placements: [] as AdPlacement[],
  },
]);

// Shared primary texts that apply to all ads
const sharedPrimaryTexts = ref<string[]>(['']);

const designerModal = ref(false);
const banners = ref<IBanner[]>([]);
const currentAdIndex = ref(0);

const pickerImages = computed(() =>
  banners.value.map((b) => {
    const state = bannerThumbnailState(b);
    return { id: b.id, ...state, disabled: !!b.render_error };
  })
);
const currentPlacementType = ref<'box' | 'portrait'>('box');
const toast = useToast();
const providerStore = useProviderStore();
const { currentOrderId } = storeToRefs(providerStore);

const updateModelValue = () => {
  // Copy shared primary texts to all ads before emitting
  const updatedAds = ads.value.map((ad) => ({
    ...ad,
    primary_texts: [...sharedPrimaryTexts.value],
  }));
  emit('update:modelValue', updatedAds);
};

const addNewTextarea = () => {
  sharedPrimaryTexts.value.push('');

  // Because multiple primary texts are supported for individual placement ads, we need to toggle the visibility of the portrait image option
  ads.value.forEach((ad, index) => {
    if (ad.ad_placements.some((p) => p.placement_group === 'stories_and_reels')) {
      // If the ad has a portrait placement, ensure the option is visible
      return;
    }
    // If the ad doesn't have a portrait placement, we can choose to show or hide the option based on the number of primary texts
    if (sharedPrimaryTexts.value.length > 1) {
      ad.individual_placement = true; // Enable individual placement if there are multiple primary texts
      // Show the option by adding the stories_and_reels placement if it doesn't exist
      ad.ad_placements.push({
        ad_id: ad.id,
        media_type: 'IMAGE',
        placement_group: 'stories_and_reels',
        ad_label: 'stories_and_reels',
        video_id: null,
        image_id: 0,
      });
    }
    ads.value[index] = ad;
  });

  updateModelValue();
};

const removePrimaryText = (textIndex: number) => {
  if (sharedPrimaryTexts.value.length <= 1) return; // Prevent removing the last primary text
  sharedPrimaryTexts.value.splice(textIndex, 1);

  // If after removal there's only one primary text left, we should remove all portrait placements from ads if there wasn't an image picked
  if (sharedPrimaryTexts.value.length === 1) {
    ads.value.map((ad) => {
      ad.ad_placements = ad.ad_placements.filter((p) => p.placement_group !== 'stories_and_reels' || p.image_id !== 0);
    });
  }

  updateModelValue();
};

const removeBoxFormat = (ad: Ad) => {
  ad.ad_placements = ad.ad_placements.filter((p) => p.placement_group !== 'feed');
  updateModelValue();
};

const removePortraitFormat = (ad: Ad) => {
  {
    if (sharedPrimaryTexts.value.length > 1) {
      toast.info(translator.get('campaigns.facebook.toast_info'));
    } else {
      ad.ad_placements = ad.ad_placements.filter((p) => p.placement_group !== 'stories_and_reels');
      ad.individual_placement = false; // Reset individual placement if portrait is removed

      updateModelValue();
    }
  }
};

const updatePrimaryText = (textIndex: number, value: string) => {
  sharedPrimaryTexts.value.splice(textIndex, 1, value);
  updateModelValue();
};

const getBannerStateByImageId = (imageId: number | null) => {
  const banner = banners.value.find((b) => b.render_image_id === imageId);
  return bannerDisplayState(banner, imageId);
};

const openPicker = (adIndex: number, placementType: 'box' | 'portrait') => {
  currentAdIndex.value = adIndex;
  currentPlacementType.value = placementType;
  designerModal.value = true;
  fetchOrderBanners(currentOrderId.value ?? '').then((res) => {
    banners.value =
      res?.data?.filter((banner: IBanner) => {
        return !!banner.designer_template_id;
      }) || [];
  });
};

const selectImage = (banner: IBanner) => {
  const adIndex = currentAdIndex.value;
  const placementType = currentPlacementType.value;
  const hasVideo = !!banner.render_video_id;

  // Find existing placement for this type or create new one
  let placement = ads.value[adIndex].ad_placements.find(
    (p) => p.placement_group === (placementType === 'box' ? 'feed' : 'stories_and_reels')
  );

  if (!placement) {
    placement = {
      ad_id: ads.value[adIndex].id,
      media_type: hasVideo ? 'VIDEO' : 'IMAGE',
      placement_group: placementType === 'box' ? 'feed' : 'stories_and_reels',
      ad_label: placementType === 'box' ? 'feed' : 'stories_and_reels',
      video_id: hasVideo ? banner.render_video_id : null,
      // 0 is the placeholder this form uses for "no image picked yet"
      image_id: banner.render_image_id ?? 0,
    };
    ads.value[adIndex].ad_placements.push(placement);
  } else {
    placement.media_type = hasVideo ? 'VIDEO' : 'IMAGE';
    placement.video_id = hasVideo ? banner.render_video_id : null;
    placement.image_id = banner.render_image_id ?? 0;
  }

  updateModelValue();
  designerModal.value = false;
};

const currentlySelectedIds = computed(() => {
  const placement = ads.value[currentAdIndex.value]?.ad_placements?.find(
    (p) => p.placement_group === (currentPlacementType.value === 'box' ? 'feed' : 'stories_and_reels')
  );
  return placement?.image_id ? [placement.image_id] : [];
});

const selectImageById = (id: number) => {
  const banner = banners.value.find((b) => b.id === id);
  if (banner) selectImage(banner);
};

const getSelectedImageId = (adIndex: number, placementType: 'box' | 'portrait') => {
  const placement = ads.value[adIndex]?.ad_placements?.find(
    (p) => p.placement_group === (placementType === 'box' ? 'feed' : 'stories_and_reels')
  );
  return placement?.image_id || null;
};

const showPortraitImage = (adIndex: number) => {
  // Show portrait image option if the user clicked the plus icon, or if there's already a portrait image selected, оr if the individual_placement is true
  return (
    ads.value[adIndex]?.ad_placements?.some((p) => p.placement_group === 'stories_and_reels') ||
    ads.value[adIndex].individual_placement
  );
};

const addStoryFormat = (adIndex: number) => {
  let placement = ads.value[adIndex].ad_placements.find((p) => p.placement_group === 'stories_and_reels');
  if (!placement) {
    placement = {
      ad_id: ads.value[adIndex].id,
      media_type: 'IMAGE',
      placement_group: 'stories_and_reels',
      ad_label: 'stories_and_reels',
      video_id: null,
      image_id: 0, // Placeholder, will be set when user selects an image
    };
    ads.value[adIndex].individual_placement = true; // Enable individual placement when adding story format
    ads.value[adIndex].ad_placements.push(placement);
    updateModelValue();
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue && newValue.length > 0) {
      // Extract shared primary texts from the first ad
      const firstAd = newValue[0];
      if (firstAd.primary_texts && firstAd.primary_texts.length > 0) {
        sharedPrimaryTexts.value = [...firstAd.primary_texts];
      } else {
        sharedPrimaryTexts.value = [''];
      }

      // Set up ads with just placements (primary texts will be shared)
      ads.value = newValue.map((ad) => ({
        id: ad.id || null,
        primary_texts: [],
        individual_placement: ad.individual_placement || false,
        ad_placements: (ad.ad_placements || []) as AdPlacement[],
      }));
    } else {
      sharedPrimaryTexts.value = [''];
      ads.value = [
        {
          id: null,
          primary_texts: [],
          individual_placement: false,
          ad_placements: [] as AdPlacement[],
        },
      ];
    }
  },
  { immediate: true, deep: true }
);
</script>

<style scoped lang="scss">
.position-relative {
  position: relative;
}

.remove-absolute {
  position: absolute;
  display: none;
  top: 0px;
  right: 0px;
}

.primary-text-item {
  position: relative;

  .remove-absolute {
    top: 5px;
    right: 5px;
  }

  &:hover .remove-absolute {
    display: block;
  }

  .delete-btn-absolute {
    position: static;
    width: 30px;
    height: 30px;
  }

  & :deep(textarea.form-control) {
    padding-right: 35px;
  }
}

.other-name-section {
  margin-top: 20px;
}

.other-name-header {
  margin-top: 20px;
}

.images-grid {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.modal-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
  align-items: center;
}

.delete-btn-absolute {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0px 0px 4px 0px #00000040;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #000;
  z-index: 10;

  &:hover {
    color: #dc3545;
  }
}

:deep(.form-label) {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}
</style>
